import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyList, Pagination, PeriodSummaryRow, Spinner } from "@services/ui-components";
import LabelStatusPayment from "./LabelStatusPayment";
import { Container } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../services/ErrorService";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { formatDate, formatTime } from "@me-team/host/src/utils/utils";
import SalaryRenderer from "./SalaryRenderer";
import { calculateTotal, formatDateInString } from "./constans";
import { useGetSalaryQuery } from "@me-team/host/main/salary/graphql/salary.hooks";
import { BookingMainProxy } from "@me-team/host/main/graphql/types";
import {
    currencySingVar,
    currentBranchEmployeeIdVar,
    currentCompanyIdVar,
    currentUserEmailVar,
} from "@me-team/host/main/globalVar/state";
import useScrollToTopOnChange from "../../hooks/useScrollToTopOnChange";
import { DatesRange } from "../../interfaces/interfaces";

interface filterType {
    employeeFilter?: number;
    dateRangeFilter: DatesRange;
}

interface SalaryTableProps {
    filter: filterType;
    shouldShowOnlyOwnSalaryRows: boolean;
    canCreatePayment: boolean;
    canOpenEmployeeAll: boolean;
    canOpenEmployeeSelf: boolean;
    canOpenService: boolean;
}

const SalaryTable: React.FC<SalaryTableProps> = ({
    filter,
    shouldShowOnlyOwnSalaryRows,
    canCreatePayment,
    canOpenEmployeeAll,
    canOpenEmployeeSelf,
    canOpenService,
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentUserEmail = useReactiveVar(currentUserEmailVar);
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const currencySing = useReactiveVar(currencySingVar);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const resolvedEmployeeId = shouldShowOnlyOwnSalaryRows
        ? (filter?.employeeFilter ?? currentBranchEmployeeId ?? null)
        : (filter?.employeeFilter ?? null);

    useScrollToTopOnChange(currentPage);

    const {
        data: salaryData,
        loading,
        refetch: salaryRefetch,
    } = useGetSalaryQuery({
        variables: {
            page: currentPage,
            itemsPerPage: itemsPerPage,
            completedBookings: true,
            filterByCompany: false,
            employeeId: resolvedEmployeeId,
            email: shouldShowOnlyOwnSalaryRows ? currentUserEmail : null,
            startDate: filter?.dateRangeFilter?.startDate
                ? formatDateInString(filter?.dateRangeFilter?.startDate)
                : null,
            endDate: filter?.dateRangeFilter?.endDate ? formatDateInString(filter?.dateRangeFilter?.endDate) : null,
            companyId: +currentCompanyId,
            branchId: Number(branchId),
        },
        fetchPolicy: "network-only",
        skip: !currentCompanyId || !currentUserEmail,
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const currency = salaryData?.user?.company?.currency;
    const salary = salaryData?.user?.company?.bookings?.bookings;
    const pageCount = parseInt(salaryData?.user?.company?.bookings?.pageCount || "0");
    const totalItems = salaryData?.user?.company?.bookings?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const canOpenEmployeeLink = (employeeId?: number | null) => {
        if (!employeeId) return false;

        const isSelfEmployee = currentBranchEmployeeId === employeeId;

        return isSelfEmployee ? canOpenEmployeeAll || canOpenEmployeeSelf : canOpenEmployeeAll;
    };

    const totalSalary = salary ? calculateTotal(salary) : 0;

    if (loading) return <Spinner />;

    return (
        <>
            <Container fluid className="table-responsive scrollbar position-relative px-0 pt-1">
                <PeriodSummaryRow
                    isButton={!shouldShowOnlyOwnSalaryRows}
                    canCreatePayment={canCreatePayment}
                    totalCost={totalSalary}
                    currency={currency?.code}
                    totalItems={totalItems && totalItems}
                    refetchSalary={salaryRefetch}
                />
                {salary?.length > 0 ? (
                    <table className="table table-borderless">
                        <thead>
                            <tr className="fs-7">
                                <th className="py-3 col-2 ps-4 pe-2 align-middle">{t("Date and Time of Service")}</th>
                                <th className="py-3 px-2 col-2 align-middle">{t("Employee")}</th>
                                <th className="py-3 px-2 col-2 align-middle">{t("service")}</th>
                                <th className="py-3 px-2 col-1 align-middle">{t("Calculated")}</th>
                                <th className="py-3 px-2 col-3 align-middle text-center">{t("Payments")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {salary?.map((salaryItem: BookingMainProxy) => (
                                <tr key={salaryItem?.id} className="border-bottom">
                                    <td className="align-middle py-4 ps-4 pe-2">
                                        {formatDate(salaryItem?.date)} {formatTime(salaryItem?.time)}
                                    </td>
                                    <td className="align-middle py-4 px-2">
                                        <div className="d-flex flex-column">
                                            {canOpenEmployeeLink(salaryItem?.employee?.id) ? (
                                                <Link
                                                    to={`/employees-list/edit-employee/${salaryItem?.employee?.id}/${branchId}`}
                                                    className="text-decoration-none"
                                                >
                                                    <span>
                                                        {salaryItem?.employee?.name} {salaryItem?.employee?.surname}
                                                    </span>
                                                </Link>
                                            ) : (
                                                <span>
                                                    {salaryItem?.employee?.name} {salaryItem?.employee?.surname}
                                                </span>
                                            )}
                                            <span className="fs-7 text-grey">
                                                {salaryItem?.employee?.position?.name}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="align-middle py-4 px-2">
                                        <div className="d-flex flex-column">
                                            {canOpenService ? (
                                                <Link
                                                    to={`/services/service/${currentCompanyId}/service-list/${salaryItem?.service?.id}/edit/${branchId}`}
                                                    className="text-decoration-none"
                                                >
                                                    {salaryItem?.service?.name}
                                                </Link>
                                            ) : (
                                                <span>{salaryItem?.service?.name}</span>
                                            )}
                                            <span className="fs-7 text-grey">
                                                ({salaryItem?.service?.serviceCategoryNew?.name})
                                            </span>
                                        </div>
                                    </td>
                                    <td className="align-middle py-4 px-2">
                                        <SalaryRenderer salaryItem={salaryItem} currencySing={currencySing} />
                                    </td>
                                    <td className="align-middle text-center py-4 px-2">
                                        <LabelStatusPayment
                                            status={
                                                !!salaryItem?.salaryPayments?.salaryPayments?.length
                                                    ? "paid"
                                                    : "not-paid"
                                            }
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <EmptyList title="The list of salary is empty"></EmptyList>
                )}

                {salaryData?.user?.company?.bookings?.totalCount > 0 ? (
                    <div className="d-flex justify-content-between align-items-center mt-4 mb-5">
                        <Pagination
                            t={t}
                            pages={Number(pageCount)}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                            firstItemIndex={firstItemIndex}
                            lastItemIndex={lastItemIndex}
                            totalItems={totalItems}
                        />
                    </div>
                ) : null}

                {isErrorModalOpen ? (
                    <ErrorModalComponent
                        i18n={i18next}
                        onClose={() => {
                            setIsErrorModalOpen(null);
                        }}
                        isOpen={!!isErrorModalOpen}
                        currentError={isErrorModalOpen}
                    />
                ) : null}
            </Container>
        </>
    );
};

export default SalaryTable;
